import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";

import {
  getOfferApi,
  createOfferApi,
  updateOfferApi,
  deleteOfferApi,
} from "../services/apicollection";

const OfferManagement = () => {
  
  const [offers, setOffers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedCourses, setSelectedCourses] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  // ================= FETCH COURSES (for the course picker) =================
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const res = await axiosInstance.get(apiUrl.GETCOURSES);
        setCourses(res.data?.data || res.data || []);
      } catch (err) {
        // course picker just stays empty — offer creation still works
      }
    };
    loadCourses();
  }, []);

  const toggleCourse = (courseId) => {
    const id = String(courseId);
    setSelectedCourses((prev) =>
      prev.some((existing) => String(existing) === id)
        ? prev.filter((existing) => String(existing) !== id)
        : [...prev, id]
    );
  };

  // ================= FETCH OFFERS =================
  const fetchOffers = useCallback(async (signal) => {
    setLoading(true);

    try {
      const res = await getOfferApi({ signal });

      if (res?.status) {
        setOffers(res?.data || []);
      } else {
        setOffers([]);
        toast.error(res?.message || "Failed to fetch offers");
      }
    } catch (error) {
      console.log(error);

      if (error.name !== "CanceledError") {
        toast.error("Something went wrong");
      }

      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ================= USE EFFECT =================
  useEffect(() => {
    const controller = new AbortController();

    fetchOffers(controller.signal);

    return () => {
      controller.abort();
    };
  }, [fetchOffers]);

  // ================= SUBMIT FORM =================
  const onSubmit = async (data) => {
    try {
      let res;
      const payload = {
        ...data,
        discountPercent: Number(data.discountPercent) || 0,
        courses: selectedCourses,
      };

      // UPDATE
      if (editId) {
        res = await updateOfferApi(editId, payload);

        if (res?.status) {
          toast.success("Offer updated successfully");
        } else {
          toast.error(res?.message || "Update failed");
          return;
        }
      }

      // CREATE
      else {
        res = await createOfferApi(payload);

        if (res?.status) {
          toast.success("Offer created successfully");
        } else {
          toast.error(res?.message || "Create failed");
          return;
        }
      }

      // RESET FORM
      reset({
        title: "",
        subtitle: "",
        discountPercent: "",
      });
      setSelectedCourses([]);

      setEditId(null);

      fetchOffers();
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong");
    }
  };

  // ================= EDIT =================
  const handleEdit = (offer) => {
    setEditId(offer._id);

    reset({
      title: offer.title,
      subtitle: offer.subtitle,
      discountPercent: offer.discountPercent || 0,
    });
    setSelectedCourses(
      (offer.courses || []).map((c) =>
        String(typeof c === "string" ? c : c._id)
      )
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this offer?"
    );

    if (!confirmDelete) return;

    try {
      const res = await deleteOfferApi(id);

      if (res?.status) {
        toast.success("Offer deleted successfully");

        setOffers((prev) => prev.filter((item) => item._id !== id));
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong");
    }
  };
return (
  <div className="min-h-screen bg-[#13233a] p-4 md:p-6 lg:p-8">
    {/* <ToastContainer position="top-right" autoClose={2000} /> */}

    {/* MAIN LAYOUT */}
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6">
      
      {/* ================= LEFT SIDE FORM ================= */}
      <div className="w-full md:w-[35%] lg:w-[30%]">
        <div className="bg-[#0f1c2e] rounded-2xl shadow-lg p-5 md:sticky md:top-5">
          
          <h2 className="text-2xl lg:text-3xl font-bold text-center mb-6">
            {editId ? "Update Offer" : "Create Offer"}
          </h2>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-5"
          >
            {/* TITLE */}
            <div>
              <label className="block mb-2 font-semibold">
                Title
              </label>

              <input
                type="text"
                placeholder="Enter title"
                {...register("title", {
                  required: "Title is required",
                })}
                className="w-full bg-[#0d1220] border border-[#1d3350] rounded-lg px-4 py-3 outline-none focus:border-blue-500 text-slate-100 placeholder:text-slate-500"
              />

              {errors.title && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* SUBTITLE */}
            <div>
              <label className="block mb-2 font-semibold">
                Subtitle
              </label>

              <textarea
                rows="5"
                placeholder="Enter subtitle"
                {...register("subtitle", {
                  required: "Subtitle is required",
                })}
                className="w-full bg-[#0d1220] border border-[#1d3350] rounded-lg px-4 py-3 outline-none focus:border-blue-500 resize-none text-slate-100 placeholder:text-slate-500"
              />

              {errors.subtitle && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.subtitle.message}
                </p>
              )}
            </div>

            {/* DISCOUNT % */}
            <div>
              <label className="block mb-2 font-semibold">
                Discount %{" "}
                <span className="text-slate-400 text-sm font-normal">
                  (0 if this is just a banner, no price change)
                </span>
              </label>

              <input
                type="number"
                min="0"
                max="100"
                placeholder="e.g. 20"
                {...register("discountPercent")}
                className="w-full bg-[#0d1220] border border-[#1d3350] rounded-lg px-4 py-3 outline-none focus:border-blue-500 text-slate-100 placeholder:text-slate-500"
              />
            </div>

            {/* COURSE PICKER */}
            <div>
              <label className="block mb-2 font-semibold">
                Applies to Courses{" "}
                <span className="text-slate-400 text-sm font-normal">
                  (leave empty = no course gets a discount)
                </span>
              </label>
              <div className="max-h-48 overflow-y-auto border border-[#1d3350] rounded-lg p-3 space-y-2">
                {courses.length === 0 ? (
                  <p className="text-slate-400 text-sm">Loading courses...</p>
                ) : (
                  courses.map((c) => (
                    <label
                      key={c._id}
                      className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCourses.some(
                          (id) => String(id) === String(c._id)
                        )}
                        onChange={() => toggleCourse(String(c._id))}
                      />
                      {c.courseName}
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* BUTTON */}
            <button
              type="submit"
              className={`w-full py-3 rounded-lg text-white font-semibold transition duration-300 ${
                editId
                  ? "bg-yellow-500 hover:bg-yellow-600"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {editId ? "Update Offer" : "Add Offer"}
            </button>
          </form>
        </div>
      </div>

      {/* ================= RIGHT SIDE OFFER LIST ================= */}
      <div className="w-full md:w-[65%] lg:w-[70%]">
        <div className="bg-[#0f1c2e] rounded-2xl shadow-lg p-5">
          
          <h2 className="text-2xl lg:text-3xl font-bold text-center mb-8">
            All Offers
          </h2>

          {/* LOADING */}
          {loading ? (
            <div className="text-center text-lg font-semibold">
              Loading offers...
            </div>
          ) : offers.length === 0 ? (
            <div className="text-center text-slate-400">
              No offers found
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              
              {offers.map((offer) => (
                <div
                  key={offer._id}
                  className="bg-[#0d1b2e] border rounded-2xl p-5 hover:shadow-lg transition duration-300"
                >
                  {/* TITLE */}
                  <h3 className="text-xl font-bold capitalize mb-3 break-words">
                    {offer.title}
                  </h3>

                  {/* SUBTITLE */}
                  <p className="text-slate-400 mb-3 break-words">
                    {offer.subtitle}
                  </p>

                  {/* DISCOUNT + COURSES */}
                  {offer.discountPercent > 0 && (
                    <p className="text-green-400 font-semibold mb-2">
                      {offer.discountPercent}% OFF
                    </p>
                  )}
                  {offer.courses?.length > 0 && (
                    <p className="text-slate-400 text-sm mb-3">
                      Applies to:{" "}
                      {offer.courses.map((c) => c.courseName || c).join(", ")}
                    </p>
                  )}

                  {/* DEBUG: exact raw API data for this offer — no
                      interpretation, no caching, straight from the last
                      fetch. Screenshot THIS if something looks wrong. */}
                  <details className="mb-3">
                    <summary className="text-xs text-amber-400 cursor-pointer select-none">
                      🔍 Debug: raw data from API
                    </summary>
                    <pre className="text-[10px] text-amber-200 bg-black/40 p-2 rounded mt-1 overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(
  {
    _id: offer._id,
    discountPercent: offer.discountPercent,
    discountPercentType: typeof offer.discountPercent,
    isActive: offer.isActive,
    coursesCount: offer.courses?.length || 0,
    courses: offer.courses,
  },
  null,
  2
)}
                    </pre>
                  </details>

                  {/* DATE */}
                  <p className="text-sm text-gray-400 mb-4">
                    Created:{" "}
                    {new Date(
                      offer.createdAt
                    ).toLocaleDateString()}
                  </p>

                  {/* BUTTONS */}
                  <div className="flex flex-wrap items-center gap-3">
                    
                    {/* EDIT */}
                    <button
                      onClick={() => handleEdit(offer)}
                      className="flex-1 min-w-[100px] bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg"
                    >
                      Edit
                    </button>

                    {/* DELETE */}
                    <button
                      onClick={() =>
                        handleDelete(offer._id)
                      }
                      className="flex-1 min-w-[100px] bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);
};

export default OfferManagement;